// src/client/renderer/vfx/items/stone-skin.ts
//
// Plan 17, Task 4.1 — Stone Skin pool (self-buff with three lifecycle phases).
//
// Triggered by THREE events:
//
//   1. ItemCastEvent (itemId='stone_skin') → phase='cast':
//        Shield grows around caster (shimmer ring, long-lived, persists until expire).
//        SINGLE-INSTANCE per caster (casterId) — re-cast releases prior shield (F6).
//        Max across all casters: 8 shields.
//
//   2. StatusEffectExpiredEvent (effectType='stoneSkin') → phase='expire':
//        Shield shatter: scale 1→1.3 spike then opacity 1→0 over 400ms.
//        Does NOT create a new emitter — transitions the EXISTING shield's timeline.
//        Returns null from acquire() (no new scene object).
//        400ms shatter tl.call cleans up: scene.remove + Map.delete + disposeGroup.
//
//   3. ItemBlockedStoneSkinEvent → phase='impact', variant='blocked':
//        Bright pulse on the existing shield (scale 1→1.15→1, opacity flash, 250ms).
//        Does NOT create a new emitter — pulses the EXISTING shield's material.
//        Returns null from acquire() (no new scene object).
//        If no shield is active for that caster → return null defensively (race condition).
//
// Colour: 0xccccff (icy blue-white shimmer).
//
// Registry tick contract (HF1):
//   pool.update(_deltaSeconds) IGNORES dt — does NOT call registry.update().
//   The caller (item-vfx-manager at Task 6.1) advances the shared registry.
//
// NOTE: pool.acquire() can have side effects on EXISTING emitters for the
//   expire and blocked phases. This is a deliberate divergence from the standard
//   acquire contract (which normally creates a new emitter). The divergence is
//   required by the spec: expire transitions the existing mesh (no flicker), and
//   blocked pulses the existing material (no new mesh). The leading comment above
//   explains this pattern.
//
// Audio:
//   Manager fires the item cue only for phase='cast'. Expire and blocked pulses
//   are intentionally silent follow-up transitions.

import * as THREE from 'three';
import type {
  GameEvent,
  ItemCastEvent,
  StatusEffectExpiredEvent,
} from '../../../../shared/game/types';
import type { ItemVfxPool, ItemVfxPoolDeps, ItemVfxSpawnArgs } from './item-vfx-types';
import { disposeSkillshotGroup } from './skillshot-shared';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const MAX_SHIELDS = 8;
const SHIELD_COLOUR = 0x8f8a7a;
const SHATTER_DURATION = 0.4;
const BLOCK_PULSE_DURATION = 0.25;

// ─────────────────────────────────────────────────────────────────────────────
// Internal state
// ─────────────────────────────────────────────────────────────────────────────

interface ShieldState {
  handle: ReturnType<ItemVfxPoolDeps['registry']['create']>;
  emitter: THREE.Group;
  /** The shield sprite — stored directly for safe access from block-pulse (SW5). */
  shieldSprite: THREE.Sprite;
  /** The sprite material used for the shield glow — exposed for pulse tweens. */
  material: THREE.SpriteMaterial;
  finished: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Build cast-phase shield emitter
// ─────────────────────────────────────────────────────────────────────────────

function buildShieldEmitter(
  anchor: { x: number; y: number; z: number },
  deps: ItemVfxPoolDeps,
  state: ShieldState,
): THREE.Group {
  const { registry } = deps;
  const group = new THREE.Group();

  // Dense stone shell sprite around the caster.
  const shieldMaterial = new THREE.SpriteMaterial({
    color: SHIELD_COLOUR,
    transparent: true,
    opacity: 0.0,
    depthWrite: false,
  });
  const shield = new THREE.Sprite(shieldMaterial);
  shield.position.set(anchor.x, anchor.y, anchor.z);
  shield.scale.set(0.0, 0.0, 1);
  group.add(shield);

  // SW5: store direct sprite reference — do NOT use children[0] later
  state.shieldSprite = shield;
  state.material = shieldMaterial;

  const handle = registry.create('stone_skin_cast');
  state.handle = handle;
  const tl = handle.timeline;

  // Grow in: scale 0→1.45, opacity 0→0.82 over 300ms
  tl.to(
    shield.scale,
    {
      x: 1.45,
      y: 1.45,
      z: 1,
      duration: 0.3,
      ease: 'back.out(1.7)',
    },
    0,
  );
  tl.to(
    shieldMaterial,
    {
      opacity: 0.82,
      duration: 0.3,
      ease: 'power1.out',
    },
    0,
  );

  // Persist indefinitely — long-lived (repeat:-1 shimmer pulse)
  // The shield stays until either expire or explicit release.
  const pulseProxy = { opacity: 0.82 };
  tl.to(
    pulseProxy,
    {
      opacity: 0.58,
      duration: 1.0,
      ease: 'sine.inOut',
      yoyo: true,
      repeat: -1,
      onUpdate: () => {
        shieldMaterial.opacity = pulseProxy.opacity;
      },
    },
    0.3,
  );

  return group;
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory — createStoneSkinPool
// ─────────────────────────────────────────────────────────────────────────────

export function createStoneSkinPool(deps: ItemVfxPoolDeps): ItemVfxPool {
  const { scene } = deps;

  /**
   * shieldMap key: casterId string.
   * One active shield per player (up to MAX_SHIELDS total).
   */
  const shieldMap = new Map<string, ShieldState>();

  let disposed = false;

  // ── acquire ──────────────────────────────────────────────────────────────

  function acquire(args: ItemVfxSpawnArgs): THREE.Object3D | null {
    if (disposed) return null;

    const casterId = args.casterId ?? '';
    const { registry } = deps;

    // ── Block pulse phase: pulses the EXISTING shield's sprite scale ─────
    //
    // HF fix: use a FRESH timeline handle (registry.create) instead of
    // appending to the cast shimmer timeline via '>'. Appending to the
    // shimmer timeline (which has repeat:-1 and has been advancing via
    // tl.time(elapsedSeconds)) will never fire in production because GSAP
    // does not replay tweens whose range is already in the past.
    //
    // SW6: target sprite.scale ONLY — the shimmer's pulseProxy already
    // drives mat.opacity via onUpdate on the cast timeline. Writing opacity
    // from a concurrent tween causes flicker. Scale spike is visually
    // distinct and safe to drive independently.
    if (args.phase === 'impact' && args.variant === 'blocked') {
      const existing = shieldMap.get(casterId);
      if (!existing) return null; // race condition — no shield active

      // SW5: use the stored sprite ref, not children[0]
      const sprite = existing.shieldSprite;
      const initialScale = sprite.scale.x;

      const pulseHandle = registry.create('stone_skin_pulse');
      const pulseTl = pulseHandle.timeline;

      // Scale spike: 1→1.15 over first half, 1.15→1 over second half
      pulseTl.to(
        sprite.scale,
        {
          x: initialScale * 1.15,
          y: initialScale * 1.15,
          z: initialScale * 1.15,
          duration: BLOCK_PULSE_DURATION * 0.5,
          ease: 'power2.out',
        },
        0,
      );
      pulseTl.to(
        sprite.scale,
        {
          x: initialScale,
          y: initialScale,
          z: initialScale,
          duration: BLOCK_PULSE_DURATION * 0.5,
          ease: 'power1.in',
        },
        BLOCK_PULSE_DURATION * 0.5,
      );

      // Self-dispose when animation completes — prevents handle leak
      pulseTl.call(
        () => {
          pulseHandle.dispose();
        },
        undefined,
        BLOCK_PULSE_DURATION,
      );

      return null; // No new emitter
    }

    // ── Expire phase: transitions existing shield to shatter ─────────────
    if (args.phase === 'expire') {
      const existing = shieldMap.get(casterId);
      if (!existing) return null; // race condition — no shield active

      // Kill the existing (possibly repeating) handle to stop the pulse loop
      existing.handle.dispose();

      // Create a fresh shatter handle for the same emitter
      const shatterHandle = registry.create('stone_skin_shatter');
      existing.handle = shatterHandle;
      const tl = shatterHandle.timeline;

      // Shatter: scale 1.2→1.56 (1.3× current), opacity 0.6→0 over 400ms
      const emitter = existing.emitter;
      // SW5: use stored sprite ref instead of children[0]
      const sprite = existing.shieldSprite;
      const mat = existing.material;

      const shatterProxy = { scale: sprite.scale.x, opacity: mat.opacity };
      tl.to(
        shatterProxy,
        {
          scale: shatterProxy.scale * 1.3,
          opacity: 0.0,
          duration: SHATTER_DURATION,
          ease: 'power2.out',
          onUpdate: () => {
            sprite.scale.set(shatterProxy.scale, shatterProxy.scale, 1);
            mat.opacity = shatterProxy.opacity;
          },
        },
        0,
      );

      // Clean up after shatter completes
      tl.call(
        () => {
          shieldMap.delete(casterId);
          scene.remove(emitter);
          disposeSkillshotGroup(emitter);
          shatterHandle.dispose();
        },
        undefined,
        SHATTER_DURATION,
      );

      return null; // No new emitter (same emitter continues)
    }

    // ── Cast phase: create new shield ────────────────────────────────────
    const chargeKey = casterId;

    // F6: release-first — release any prior shield for this caster
    const prior = shieldMap.get(chargeKey);
    if (prior) {
      prior.handle.dispose();
      scene.remove(prior.emitter);
      disposeSkillshotGroup(prior.emitter);
      shieldMap.delete(chargeKey);
    }

    // Max-active guard (across all casters)
    if (shieldMap.size >= MAX_SHIELDS) return null;

    const anchor = args.casterPosition ?? { x: 0, y: 0, z: 0 };
    const state: ShieldState = {
      handle: null!,
      emitter: null!,
      shieldSprite: null!,
      material: null!,
      finished: false,
    };
    const group = buildShieldEmitter(anchor, deps, state);
    state.emitter = group;
    scene.add(group);
    shieldMap.set(chargeKey, state);
    return group;
  }

  // ── update ───────────────────────────────────────────────────────────────
  // Registry tick contract (HF1): pool.update does NOT advance the registry.

  function update(_deltaSeconds: number): ReadonlySet<THREE.Object3D> {
    const all = new Set<THREE.Object3D>();
    for (const state of shieldMap.values()) {
      all.add(state.emitter);
    }
    return all;
  }

  // ── isFinished ───────────────────────────────────────────────────────────

  function isFinished(emitter: THREE.Object3D): boolean {
    for (const state of shieldMap.values()) {
      if (state.emitter === emitter) return state.finished;
    }
    return true;
  }

  // ── release ──────────────────────────────────────────────────────────────

  function release(emitter: THREE.Object3D): void {
    for (const [key, state] of shieldMap.entries()) {
      if (state.emitter === emitter) {
        state.handle.dispose();
        scene.remove(emitter);
        disposeSkillshotGroup(emitter);
        shieldMap.delete(key);
        return;
      }
    }
  }

  // ── dispose (F4) ─────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;
    for (const state of shieldMap.values()) {
      state.handle.dispose();
      scene.remove(state.emitter);
      disposeSkillshotGroup(state.emitter);
    }
    shieldMap.clear();
  }

  return { acquire, update, isFinished, release, dispose };
}

// ─────────────────────────────────────────────────────────────────────────────
// Event resolver — resolveStoneSkinForEvent
// ─────────────────────────────────────────────────────────────────────────────

export function resolveStoneSkinForEvent(
  event: GameEvent,
  getPositionForPlayer?: (playerId: string) => { x: number; y: number; z: number } | undefined,
): { poolId: 'stone_skin'; args: ItemVfxSpawnArgs } | null {
  void getPositionForPlayer;

  // item_cast with stone_skin → cast phase
  if (event.type === 'item_cast') {
    const cast = event as ItemCastEvent;
    if (cast.itemId !== 'stone_skin') return null;
    return null;
  }

  // status_effect_expired with stoneSkin → expire phase
  if (event.type === 'status_effect_expired') {
    const expired = event as StatusEffectExpiredEvent;
    if (expired.effectType !== 'stoneSkin') return null;
    return null;
  }

  // item_blocked_stoneskin → block pulse phase
  if (event.type === 'item_blocked_stoneskin') {
    return null;
  }

  return null;
}
